import React, {useEffect, useId, useState} from "react";
import PropTypes from "prop-types";
import {Modal} from "../../components/Modal";
import {i18nFormat} from "../../lib/i18nFormat";

export function SelfHostedServerEditCustomDomainDialog(props) {
  const settingUpCustomDomainUrl = "https://support.homey.app/hc/en-us/articles/24060221985436-Setting-up-a-Custom-Domain-for-Homey-Self-Hosted-Server"
  const customDomainId = useId();
  const [customDomain, setCustomDomain] = useState({ input: "", valid: true });
  const [isChecking, setIsChecking] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const [error, setError] = useState("");

  function isValidURL(input) {
    try {
      new URL(input);
      return true;
    } catch (error) {
      return false;
    }
  }

  useEffect(() => {
    setCustomDomain({
      input: typeof props.server.selfHostedServerCustomHostname === "string" ? props.server.selfHostedServerCustomHostname : "",
      saved: typeof props.server.selfHostedServerCustomHostname === "string",
      valid: true
    });
    setError("");
  }, [props.server, props.isOpen]);

  async function handleSaveDomainClick() {
    setError("");
    const minLoadingTime = 500; // make sure at least 500ms loading to avoid flicker
    const startTime = Date.now();
    const candidateUrl = `https://${customDomain.input}`;
    const isValid = isValidURL(candidateUrl);

    setCustomDomain({
      ...customDomain,
      valid: isValid
    });

    if (!isValid) {
      setError(i18nFormat("shs.edit-custom-domain-dialog.invalid-url"));
      return;
    }

    try {
      setIsChecking(true);
      await window.hyWebsite.cloudApi.updateHomeySelfHostedServerCustomHostname({
        homeyId: props.server.id,
        customHostname: customDomain.input,
      });
      await window.hyWebsite.cloudApi.refreshUser();

      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
      }
      setCustomDomain({
        ...customDomain,
        saved: true
      });
      props.onClose();
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
      }
      setError(error.message);
    } finally {
      setIsChecking(false);
    }
  }

  async function handleResetDomainClick() {
    setError("");
    const minLoadingTime = 500; // make sure at least 500ms loading to avoid flicker
    const startTime = Date.now();

    try {
      setIsResetting(true);
      await window.hyWebsite.cloudApi.deleteHomeySelfHostedServerCustomHostname({
        homeyId: props.server.id,
      });
      await window.hyWebsite.cloudApi.refreshUser();

      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
      }
      setCustomDomain({
        input: "",
        saved: false,
        valid: true
      });
      props.onClose();
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
      }
      setError(error.message);
    } finally {
      setIsResetting(false);
    }
  }

  function handleTextInput(event) {
    setCustomDomain({
      ...customDomain,
      input: event.target.value,
      saved: typeof props.server.selfHostedServerCustomHostname === "string" && props.server.selfHostedServerCustomHostname === event.target.value,
      valid: true
    });

    if (error) setError(""); // Reset error state on input change
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="account-shs-edit-custom-domain-dialog">
        <button type="button" className="account-shs-edit-custom-domain-dialog__close-button glyph-core-cross"
                onClick={props.onClose}>
        </button>
        <form className="account-shs-edit-custom-domain-dialog__form">
          <h2 className="account-shs-edit-custom-domain-dialog__title">
            {i18nFormat("shs.edit-custom-domain-dialog.title")}
          </h2>
          <p className="account-shs-edit-custom-domain-dialog__description">
            {i18nFormat("shs.edit-custom-domain-dialog.description")}
            &nbsp;
            <a className="account-shs-edit-custom-domain-dialog__link" href={settingUpCustomDomainUrl}
               target="_blank" rel="noopener noreferrer">
              <span>{i18nFormat("shs.edit-custom-domain-dialog.learn-more")}</span>
              <span className="glyph-core-square-arrow-up-right"/>
            </a>
          </p>

          <div
            className="account-shs-edit-custom-domain-dialog__input-wrapper">
            <label className={`account-shs-edit-custom-domain-dialog__label ${!customDomain.valid ? "is-invalid" : ""}`}
                   htmlFor={customDomainId}>
              {"https://"}
              <input
                type="text"
                id={customDomainId}
                name="custom-domain"
                className="account-shs-edit-custom-domain-dialog__input"
                value={customDomain.input}
                onInput={handleTextInput}
                placeholder={"example.domain.com"}
              />
            </label>
            <span className="account-shs-edit-custom-domain-dialog__error">{error}</span>
          </div>
          <div className="account-shs-edit-custom-domain-dialog__buttons">
            <button type="submit"
                    data-is-loading={isChecking}
                    data-is-saved={customDomain.saved}
                    data-is-invalid={!customDomain.valid}
                    className="account-shs-edit-custom-domain-dialog__submit gradient-button-blue"
                    onClick={handleSaveDomainClick}
                    disabled={isChecking || customDomain.saved || !customDomain.valid || isResetting}>
              {isChecking && (<span className="account-shs-edit-custom-domain-dialog__loading-throbber"/>)}
              <span>{isChecking ? i18nFormat("shs.edit-custom-domain-dialog.checking-domain") : i18nFormat("shs.edit-custom-domain-dialog.save-domain")}</span>
            </button>
            {typeof props.server.selfHostedServerCustomHostname === "string" && (
              <button type="reset"
                      data-is-loading={isResetting}
                      className="account-shs-edit-custom-domain-dialog__submit flat-button flat-button--light-red"
                      onClick={handleResetDomainClick}
                      disabled={isResetting}>
                {isResetting && (<span className="account-shs-edit-custom-domain-dialog__loading-throbber"/>)}
                <span>{isResetting ? i18nFormat("shs.edit-custom-domain-dialog.resetting-domain") : i18nFormat("shs.edit-custom-domain-dialog.reset-domain")}</span>
              </button>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
}

SelfHostedServerEditCustomDomainDialog.propTypes = {
  server: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    selfHostedServerSubscriptionActive: PropTypes.bool.isRequired,
    selfHostedServerSubscriptionType: PropTypes.oneOf(["trial", "lifetime", "monthly"]).isRequired,
    selfHostedServerSubscriptionExpiresAt: PropTypes.string,
    selfHostedServerCustomHostname: PropTypes.string,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};